import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import {
  SITE_NAME,
  SITE_URL,
  DEFAULT_DESCRIPTION,
  DEFAULT_IMAGE,
  DEFAULT_KEYWORDS,
} from "../seoConfig";

/**
 * Componente SEO reutilizável.
 * Props principais:
 *  - title: título específico da página (sem o nome da marca)
 *  - description: descrição da página
 *  - image: URL absoluta da imagem de compartilhamento
 *  - url: URL canônica da página
 *  - canonical: força uma URL canônica (fallback para url)
 *  - type: tipo Open Graph (ex: website, article, product)
 *  - keywords: array ou string de palavras-chave
 *  - noIndex: se true, adiciona noindex,nofollow
 *  - structuredData: objeto JSON-LD customizado (senão usa Organization)
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  imageAlt = "Banner institucional Solar System Brasil mostrando economia de energia",
  url = SITE_URL,
  canonical,
  type = "website",
  keywords = DEFAULT_KEYWORDS,
  noIndex = false,
  structuredData,
  geoRegion = "BR-SP",
  geoPlaceName = "Araçatuba",
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonicalUrl = canonical || url;

  const jsonLd = structuredData || {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}#organization`,
    name: SITE_NAME,
    url: SITE_URL,
  // Usa versão quadrada para favorecer thumbnail em resultados Google (1:1)
  logo: `${SITE_URL}logo_icon.png`,
    image: {
      "@type": "ImageObject",
      url: image,
      width: 1200,
      height: 630,
    },
    sameAs: ["https://www.instagram.com/solarsystembrasil/"],
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta
        name="keywords"
        content={Array.isArray(keywords) ? keywords.join(", ") : keywords}
      />
      <meta name="author" content={SITE_NAME} />
      {noIndex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow" />
      )}
  <link rel="canonical" href={canonicalUrl} />
  <meta name="geo.region" content={geoRegion} />
  <meta name="geo.placename" content={geoPlaceName} />
  <meta name="geo.country" content="BR" />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt} />
      {/* Fallback WebP dynamic if original is JPG */}
      {image.endsWith(".jpg") && (
        <>
          <meta property="og:image" content={image.replace(/\.jpg$/i, ".webp")} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content={`${imageAlt} (versão WebP otimizada)`} />
        </>
      )}
      <meta property="og:locale" content="pt_BR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* JSON-LD Estruturado */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default SEO;

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  geoRegion: PropTypes.string,
  geoPlaceName: PropTypes.string,
  url: PropTypes.string,
  canonical: PropTypes.string,
  type: PropTypes.string,
  keywords: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  noIndex: PropTypes.bool,
  structuredData: PropTypes.object,
};

SEO.defaultProps = {
  description: DEFAULT_DESCRIPTION,
  image: DEFAULT_IMAGE,
  imageAlt: "Banner institucional Solar System Brasil mostrando economia de energia",
  geoRegion: "BR-SP",
  geoPlaceName: "Araçatuba",
  url: SITE_URL,
  type: "website",
  keywords: DEFAULT_KEYWORDS,
  noIndex: false,
};
